/**
 * Tenancies: list/history, create (tenants + initial rent), edit, overview with rent
 * history, arrears, reviews, deposit, and linked activity.
 */
import {
  DEPOSIT_SCHEMES,
  RENT_CHANGE_REASONS,
  RENT_FREQUENCIES,
  TENANCY_STATUSES,
  TENANCY_TYPES,
} from "@shared/constants.ts";
import { annualise } from "@shared/finance/rent.ts";
import {
  endTenancySchema,
  rentPeriodSchema,
  rentReviewSchema,
  tenancyChecks,
  tenancyCreateSchema,
  tenancySchema,
} from "@shared/schemas/portfolio.ts";
import { Badge, Button, Card, Col, Row, Table } from "react-bootstrap";
import { useFieldArray } from "react-hook-form";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { Attachments } from "../../components/Attachments.tsx";
import { AuditHistory } from "../../components/AuditHistory.tsx";
import { EntityForm } from "../../components/forms/EntityForm.tsx";
import {
  type AnyForm,
  CheckboxField,
  DateField,
  MoneyField,
  NumberField,
  SelectField,
  TextAreaField,
  TextField,
} from "../../components/forms/fields.tsx";
import { ModalFormButton } from "../../components/forms/ModalForm.tsx";
import { type Column, DataTable } from "../../components/ui/DataTable.tsx";
import { FilterBar, FilterSelect, SearchInput, ViewTabs } from "../../components/ui/Filters.tsx";
import { Icon } from "../../components/ui/Icon.tsx";
import {
  Can,
  ConfirmButton,
  DefinitionList,
  EmptyState,
  IconButtonLink,
  Money,
  PageHeader,
  QueryState,
  Section,
  StatCard,
  StatusBadge,
} from "../../components/ui/kit.tsx";
import { api, type Params } from "../../lib/api.ts";
import { useAuth } from "../../lib/auth.tsx";
import { useFormat } from "../../lib/format.ts";
import { toOptions, useLookups } from "../../lib/lookups.ts";
import { useApi, useInvalidateAll } from "../../lib/query.ts";
import { useSortState, useUrlState } from "../../lib/url-state.ts";
import { IncomeTable } from "../money/IncomePages.tsx";
import { CommunicationTable } from "../operations/CommunicationPages.tsx";
import { MaintenanceTable } from "../operations/MaintenancePages.tsx";
import { TaskTable } from "../operations/TaskPages.tsx";

export interface TenancyRow {
  id: string;
  propertyId: string;
  reference: string;
  tenancyType: string;
  startDate: string;
  fixedTermEndDate: string | null;
  endDate: string | null;
  noticeGivenDate: string | null;
  endReason: string;
  status: string;
  addressLine1: string;
  postcode: string;
  tenantNames: string | null;
  currentRentPence: number | null;
  currentRentFrequency: string | null;
  depositPence: number | null;
  depositScheme: string | null;
  depositReference: string;
  depositProtectedDate: string | null;
  depositReturnedDate: string | null;
  depositReturnedPence: number | null;
  depositNotes: string;
  notes: string;
}

export function TenancyTable({ filters, showProperty = true }: { filters: Params; showProperty?: boolean }) {
  const f = useFormat();
  const { sort, onSort } = useSortState();
  const q = useApi<TenancyRow[]>("/api/tenancies", { ...filters, sort: sort.sort, dir: sort.dir });
  const columns: Column<TenancyRow>[] = [
    ...(showProperty
      ? [
          {
            key: "property",
            header: "Property",
            sort: "address",
            cell: (r: TenancyRow) => r.addressLine1,
          } satisfies Column<TenancyRow>,
        ]
      : []),
    { key: "tenants", header: "Tenants", cell: (r) => r.tenantNames ?? "—" },
    { key: "start", header: "Start", sort: "start", cell: (r) => f.date(r.startDate) },
    {
      key: "end",
      header: "End",
      sort: "end",
      cell: (r) =>
        r.endDate ? (
          f.date(r.endDate)
        ) : r.fixedTermEndDate ? (
          <span className="text-muted">Fixed term to {f.date(r.fixedTermEndDate)}</span>
        ) : (
          "—"
        ),
    },
    {
      key: "rent",
      header: "Rent",
      sort: "rent",
      align: "end",
      cell: (r) =>
        r.currentRentPence === null ? (
          "—"
        ) : (
          <>
            <Money pence={r.currentRentPence} />{" "}
            <span className="small text-muted">
              {f.label(RENT_FREQUENCIES, r.currentRentFrequency).toLowerCase()}
            </span>
          </>
        ),
    },
    {
      key: "status",
      header: "Status",
      cell: (r) => <StatusBadge status={r.status} label={f.label(TENANCY_STATUSES, r.status)} />,
    },
  ];
  return (
    <QueryState query={q}>
      {(rows) => (
        <DataTable
          rows={rows}
          columns={columns}
          rowKey={(r) => r.id}
          rowLink={(r) => `/tenancies/${r.id}`}
          sort={sort}
          onSort={onSort}
          empty={
            <EmptyState
              icon="key"
              title="No tenancies"
              action={
                <IconButtonLink
                  to={`/tenancies/new${filters.propertyId ? `?propertyId=${filters.propertyId}` : ""}`}
                  icon="plus"
                >
                  Add tenancy
                </IconButtonLink>
              }
            >
              Add a tenancy to record tenants, rent, deposit and dates.
            </EmptyState>
          }
        />
      )}
    </QueryState>
  );
}

export function TenancyListPage() {
  const { get } = useUrlState();
  const lookups = useLookups();
  const status = get("status") || "current";
  return (
    <>
      <PageHeader
        title="Tenancies"
        subtitle="Current, upcoming and historical tenancies"
        actions={
          <Can permission="tenancies:write">
            <IconButtonLink to="/tenancies/new" icon="plus">
              Add tenancy
            </IconButtonLink>
          </Can>
        }
      />
      <Card>
        <FilterBar>
          <ViewTabs
            param="status"
            defaultValue="current"
            options={[
              { value: "current", label: "Current" },
              { value: "upcoming", label: "Upcoming" },
              { value: "ended", label: "Historical" },
              { value: "all", label: "All" },
            ]}
          />
          <SearchInput placeholder="Search tenant, address" />
          <FilterSelect param="propertyId" label="Property" options={toOptions(lookups?.properties)} />
        </FilterBar>
        <TenancyTable
          filters={{ status: status === "all" ? "" : status, propertyId: get("propertyId"), q: get("q") }}
        />
      </Card>
    </>
  );
}

function TenantsFieldArray({ form }: { form: AnyForm }) {
  const lookups = useLookups();
  const { fields, append, remove } = useFieldArray({ control: form.control, name: "tenants" });
  return (
    <Section
      title="Tenants"
      actions={
        <Button
          size="sm"
          variant="outline-primary"
          onClick={() => append({ tenantId: "", name: "", email: "", phone: "", isLead: false })}
        >
          <Icon name="plus" size={14} /> Add tenant
        </Button>
      }
    >
      {fields.map((field, i) => {
        const existing = form.watch(`tenants.${i}.tenantId`);
        return (
          <Row key={field.id} className="align-items-end border-bottom pb-2 mb-3">
            <Col md={4}>
              <SelectField
                name={`tenants.${i}.tenantId`}
                label="Existing tenant"
                options={toOptions(lookups?.tenants)}
                placeholder="New tenant…"
              />
            </Col>
            {!existing && (
              <>
                <Col md={3}>
                  <TextField name={`tenants.${i}.name`} label="Full name" required />
                </Col>
                <Col md={3}>
                  <TextField name={`tenants.${i}.email`} label="Email" type="email" />
                </Col>
                <Col md={2}>
                  <TextField name={`tenants.${i}.phone`} label="Phone" />
                </Col>
              </>
            )}
            <Col md={existing ? 8 : 12} className="d-flex justify-content-between align-items-center mb-3">
              <CheckboxField name={`tenants.${i}.isLead`} label="Lead tenant" />
              {fields.length > 1 && (
                <Button size="sm" variant="link" className="text-danger" onClick={() => remove(i)}>
                  Remove
                </Button>
              )}
            </Col>
          </Row>
        );
      })}
    </Section>
  );
}

export function TenancyFormPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const lookups = useLookups();
  const today = new Date().toISOString().slice(0, 10);
  const schema = id ? tenancySchema : tenancyCreateSchema;
  return (
    <EntityForm
      schema={schema as typeof tenancyCreateSchema}
      checks={[tenancyChecks]}
      resource="/api/tenancies"
      id={id}
      title={id ? "Edit tenancy" : "Add tenancy"}
      breadcrumbs={[{ label: "Tenancies", to: "/tenancies" }, { label: id ? "Edit" : "New" }]}
      defaults={{
        propertyId: params.get("propertyId") ?? "",
        reference: "",
        tenancyType: "ast",
        startDate: today,
        fixedTermEndDate: "",
        noticeGivenDate: "",
        depositPence: "",
        depositScheme: "",
        depositReference: "",
        depositProtectedDate: "",
        depositReturnedDate: "",
        depositReturnedPence: "",
        depositNotes: "",
        notes: "",
        tenants: [{ tenantId: "", name: "", email: "", phone: "", isLead: true }],
        rent: {
          effectiveFromDate: today,
          amountPence: "",
          frequency: "monthly",
          dueDay: "1",
          reason: "initial",
          notes: "",
        },
      }}
      toForm={(r) => ({
        ...Object.fromEntries(Object.entries(r).map(([k, v]) => [k, v === null ? "" : v])),
        tenants: (r.tenants as Array<{ id: string; isLead: boolean }>).map((t) => ({
          tenantId: t.id,
          name: "",
          email: "",
          phone: "",
          isLead: t.isLead,
        })),
        rent: undefined,
      })}
      redirect={(saved) => `/tenancies/${saved.id}`}
      cancelTo={id ? `/tenancies/${id}` : "/tenancies"}
    >
      {(form) => (
        <>
          <Row>
            <Col md={6}>
              <SelectField
                name="propertyId"
                label="Property"
                options={toOptions(lookups?.properties)}
                placeholder="Choose a property"
                required
              />
            </Col>
            <Col md={3}>
              <SelectField name="tenancyType" label="Tenancy type" options={TENANCY_TYPES} required />
            </Col>
            <Col md={3}>
              <TextField name="reference" label="Reference" />
            </Col>
            <Col md={4}>
              <DateField name="startDate" label="Start date" required />
            </Col>
            <Col md={4}>
              <DateField
                name="fixedTermEndDate"
                label="Fixed term ends"
                hint="Leave blank for periodic tenancies."
              />
            </Col>
            <Col md={4}>
              <DateField name="noticeGivenDate" label="Notice given" />
            </Col>
          </Row>
          <TenantsFieldArray form={form} />
          {!id && (
            <Section title="Rent">
              <Row>
                <Col md={3}>
                  <MoneyField name="rent.amountPence" label="Rent amount" hint="Leave blank to set later." />
                </Col>
                <Col md={3}>
                  <SelectField name="rent.frequency" label="Frequency" options={RENT_FREQUENCIES} />
                </Col>
                <Col md={3}>
                  <NumberField name="rent.dueDay" label="Due day of month" min={1} max={31} />
                </Col>
                <Col md={3}>
                  <DateField name="rent.effectiveFromDate" label="Rent starts" />
                </Col>
              </Row>
            </Section>
          )}
          <Section title="Deposit">
            <Row>
              <Col md={3}>
                <MoneyField name="depositPence" label="Deposit amount" />
              </Col>
              <Col md={3}>
                <SelectField
                  name="depositScheme"
                  label="Protection scheme"
                  options={DEPOSIT_SCHEMES}
                  placeholder="—"
                />
              </Col>
              <Col md={3}>
                <TextField name="depositReference" label="Scheme reference" />
              </Col>
              <Col md={3}>
                <DateField name="depositProtectedDate" label="Date protected" />
              </Col>
              {id && (
                <>
                  <Col md={3}>
                    <DateField name="depositReturnedDate" label="Date returned" />
                  </Col>
                  <Col md={3}>
                    <MoneyField name="depositReturnedPence" label="Amount returned" />
                  </Col>
                  <Col md={6}>
                    <TextField name="depositNotes" label="Deductions / notes" />
                  </Col>
                </>
              )}
            </Row>
          </Section>
          <TextAreaField name="notes" label="Notes" />
        </>
      )}
    </EntityForm>
  );
}

interface TenancyDetail extends TenancyRow {
  addressLine2: string;
  town: string;
  tenants: Array<{ id: string; name: string; email: string; phone: string; isLead: boolean }>;
  rentPeriods: Array<{
    id: string;
    effectiveFromDate: string;
    amountPence: number;
    frequency: string;
    dueDay: number | null;
    reason: string;
    notes: string;
  }>;
  rentReviews: Array<{
    id: string;
    reviewDate: string;
    currentAmountPence: number | null;
    proposedAmountPence: number | null;
    agreedAmountPence: number | null;
    effectiveFromDate: string | null;
    noticeServedDate: string | null;
    outcome: string;
    notes: string;
  }>;
  currentRent: {
    amountPence: number;
    frequency: string;
    dueDay: number | null;
    effectiveFrom: string;
  } | null;
  arrears: {
    expectedPence: number;
    receivedPence: number;
    outstandingPence: number;
    creditPence: number;
  } | null;
}

interface Schedule {
  instalments: Array<{
    dueDate: string;
    periodFrom: string;
    periodTo: string;
    amountPence: number;
    paidPence: number;
    outstandingPence: number;
    status: string;
    prorated: boolean;
  }>;
  creditPence: number;
}

function rentDefaults(current: TenancyDetail["currentRent"]) {
  return {
    effectiveFromDate: new Date().toISOString().slice(0, 10),
    amountPence: current?.amountPence ?? "",
    frequency: current?.frequency ?? "monthly",
    dueDay: current?.dueDay ?? "",
    reason: current ? "review" : "initial",
    notes: "",
  };
}

function RentFields() {
  return (
    <Row>
      <Col md={6}>
        <MoneyField name="amountPence" label="Rent amount" required />
      </Col>
      <Col md={6}>
        <SelectField name="frequency" label="Frequency" options={RENT_FREQUENCIES} required />
      </Col>
      <Col md={6}>
        <DateField name="effectiveFromDate" label="Effective from" required />
      </Col>
      <Col md={6}>
        <NumberField name="dueDay" label="Due day of month" min={1} max={31} />
      </Col>
      <Col md={12}>
        <SelectField name="reason" label="Reason" options={RENT_CHANGE_REASONS} />
        <TextField name="notes" label="Notes" />
      </Col>
    </Row>
  );
}

function RentSection({ t }: { t: TenancyDetail }) {
  const f = useFormat();
  const invalidate = useInvalidateAll();
  const { can } = useAuth();
  return (
    <Section
      title="Rent history"
      actions={
        can("tenancies:write") && (
          <ModalFormButton
            label={t.currentRent ? "Change rent" : "Set rent"}
            title={t.currentRent ? "Change rent" : "Set rent"}
            schema={rentPeriodSchema}
            defaults={rentDefaults(t.currentRent)}
            onSubmit={(v) => api.post(`/api/tenancies/${t.id}/rent`, v)}
          >
            {() => <RentFields />}
          </ModalFormButton>
        )
      }
      flush
    >
      {t.rentPeriods.length === 0 ? (
        <EmptyState icon="income" title="No rent set">
          Set the rent to track what is due and any arrears.
        </EmptyState>
      ) : (
        <Table className="mb-0 align-middle" responsive>
          <thead>
            <tr>
              <th>From</th>
              <th>To</th>
              <th className="text-end">Rent</th>
              <th>Due</th>
              <th>Reason</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {t.rentPeriods.map((p, i) => {
              const next = t.rentPeriods[i - 1];
              const isCurrent = t.currentRent?.effectiveFrom === p.effectiveFromDate;
              return (
                <tr key={p.id} className={isCurrent ? "table-primary" : undefined}>
                  <td>{f.date(p.effectiveFromDate)}</td>
                  <td>{next ? f.date(next.effectiveFromDate) : t.endDate ? f.date(t.endDate) : "Ongoing"}</td>
                  <td className="text-end">
                    <Money pence={p.amountPence} />{" "}
                    <span className="small text-muted">
                      {f.label(RENT_FREQUENCIES, p.frequency).toLowerCase()}
                    </span>
                    <div className="small text-muted">
                      {f.money(annualise(p.amountPence, p.frequency as never))} a year
                    </div>
                  </td>
                  <td>{p.dueDay ? `Day ${p.dueDay}` : "—"}</td>
                  <td>
                    {f.label(RENT_CHANGE_REASONS, p.reason)}{" "}
                    {isCurrent && <Badge bg="primary">Current</Badge>}
                  </td>
                  <td className="text-end">
                    {can("tenancies:write") && (
                      <ConfirmButton
                        title="Remove this rent period?"
                        body="Expected rent and arrears will be recalculated."
                        onConfirm={async () => {
                          await api.delete(`/api/tenancies/${t.id}/rent/${p.id}`);
                          await invalidate();
                        }}
                      >
                        <Icon name="trash" size={14} />
                      </ConfirmButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      )}
    </Section>
  );
}

function RentScheduleSection({ tenancyId }: { tenancyId: string }) {
  const f = useFormat();
  const q = useApi<Schedule>(`/api/tenancies/${tenancyId}/rent-schedule`);
  return (
    <Section title="Rent due & payments" flush>
      <QueryState query={q}>
        {(s) =>
          s.instalments.length === 0 ? (
            <p className="text-muted small p-3 mb-0">No rent due yet.</p>
          ) : (
            <div style={{ maxHeight: 360, overflowY: "auto" }}>
              <Table size="sm" className="mb-0 align-middle">
                <thead className="sticky-top bg-white">
                  <tr>
                    <th>Due</th>
                    <th>Period</th>
                    <th className="text-end">Due</th>
                    <th className="text-end">Paid</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {[...s.instalments].reverse().map((i) => (
                    <tr key={i.dueDate}>
                      <td>{f.date(i.dueDate)}</td>
                      <td className="small text-muted">
                        {f.date(i.periodFrom)} – {f.date(i.periodTo)} {i.prorated && "(pro-rata)"}
                      </td>
                      <td className="text-end">
                        <Money pence={i.amountPence} />
                      </td>
                      <td className="text-end">
                        <Money pence={i.paidPence} />
                      </td>
                      <td>
                        <StatusBadge status={i.status} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
          )
        }
      </QueryState>
    </Section>
  );
}

function ReviewsSection({ t }: { t: TenancyDetail }) {
  const f = useFormat();
  const { can } = useAuth();
  return (
    <Section
      title="Rent reviews"
      actions={
        can("tenancies:write") && (
          <ModalFormButton
            label="Record review"
            title="Record rent review"
            schema={rentReviewSchema}
            defaults={{
              reviewDate: new Date().toISOString().slice(0, 10),
              proposedAmountPence: "",
              agreedAmountPence: "",
              noticeServedDate: "",
              effectiveFromDate: "",
              outcome: "pending",
              notes: "",
            }}
            onSubmit={(v) => api.post(`/api/tenancies/${t.id}/reviews`, v)}
          >
            {() => (
              <Row>
                <Col md={6}>
                  <DateField name="reviewDate" label="Review date" required />
                </Col>
                <Col md={6}>
                  <DateField name="noticeServedDate" label="Notice served" />
                </Col>
                <Col md={6}>
                  <MoneyField name="proposedAmountPence" label="Proposed rent" />
                </Col>
                <Col md={6}>
                  <MoneyField name="agreedAmountPence" label="Agreed rent" />
                </Col>
                <Col md={6}>
                  <SelectField
                    name="outcome"
                    label="Outcome"
                    options={[
                      { value: "pending", label: "Pending" },
                      { value: "agreed", label: "Agreed" },
                      { value: "declined", label: "Declined" },
                      { value: "withdrawn", label: "Withdrawn" },
                    ]}
                  />
                </Col>
                <Col md={6}>
                  <DateField
                    name="effectiveFromDate"
                    label="New rent effective from"
                    hint="When agreed, a new rent period is created."
                  />
                </Col>
                <Col md={12}>
                  <TextAreaField name="notes" label="Notes" rows={2} />
                </Col>
              </Row>
            )}
          </ModalFormButton>
        )
      }
    >
      {t.rentReviews.length === 0 ? (
        <p className="text-muted small mb-0">No rent reviews recorded.</p>
      ) : (
        <Table size="sm" className="mb-0">
          <thead>
            <tr>
              <th>Date</th>
              <th className="text-end">Was</th>
              <th className="text-end">Proposed</th>
              <th className="text-end">Agreed</th>
              <th>Outcome</th>
            </tr>
          </thead>
          <tbody>
            {t.rentReviews.map((r) => (
              <tr key={r.id}>
                <td>{f.date(r.reviewDate)}</td>
                <td className="text-end">{f.money(r.currentAmountPence)}</td>
                <td className="text-end">{f.money(r.proposedAmountPence)}</td>
                <td className="text-end">{f.money(r.agreedAmountPence)}</td>
                <td>
                  <StatusBadge
                    status={
                      r.outcome === "agreed" ? "completed" : r.outcome === "pending" ? "open" : "cancelled"
                    }
                    label={r.outcome}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}
    </Section>
  );
}

export function TenancyOverviewPage() {
  const { id } = useParams();
  const f = useFormat();
  const navigate = useNavigate();
  const invalidate = useInvalidateAll();
  const { can } = useAuth();
  const q = useApi<TenancyDetail>(`/api/tenancies/${id}`);
  return (
    <QueryState query={q}>
      {(t) => (
        <>
          <PageHeader
            title={t.tenants.map((x) => x.name).join(" & ") || "Tenancy"}
            subtitle={
              <>
                <Link to={`/properties/${t.propertyId}`}>
                  {t.addressLine1}, {t.postcode}
                </Link>{" "}
                · {f.label(TENANCY_TYPES, t.tenancyType)} ·{" "}
                <StatusBadge status={t.status} label={f.label(TENANCY_STATUSES, t.status)} />
              </>
            }
            breadcrumbs={[{ label: "Tenancies", to: "/tenancies" }, { label: t.addressLine1 }]}
            actions={
              <>
                <Can permission="income:write">
                  <IconButtonLink to={`/income/new?propertyId=${t.propertyId}&tenancyId=${t.id}`} icon="plus">
                    Record rent received
                  </IconButtonLink>
                </Can>
                <Can permission="communications:write">
                  <IconButtonLink
                    to={`/communications/new?propertyId=${t.propertyId}&tenancyId=${t.id}&tenantId=${t.tenants[0]?.id ?? ""}`}
                    icon="message"
                    variant="outline-primary"
                  >
                    Log communication
                  </IconButtonLink>
                </Can>
                {can("tenancies:write") && !t.endDate && (
                  <ModalFormButton
                    label="End tenancy"
                    buttonSize="md"
                    variant="outline-secondary"
                    title="End tenancy"
                    schema={endTenancySchema}
                    defaults={{
                      endDate: new Date().toISOString().slice(0, 10),
                      endReason: "",
                      noticeGivenDate: t.noticeGivenDate ?? "",
                    }}
                    onSubmit={(v) => api.post(`/api/tenancies/${t.id}/end`, v)}
                    submitLabel="End tenancy"
                  >
                    {() => (
                      <>
                        <DateField
                          name="endDate"
                          label="Final day of tenancy"
                          required
                          hint="No rent is expected after this date."
                        />
                        <DateField name="noticeGivenDate" label="Notice given" />
                        <TextField name="endReason" label="Reason" placeholder="e.g. Tenant gave notice" />
                      </>
                    )}
                  </ModalFormButton>
                )}
                <Can permission="tenancies:write">
                  <IconButtonLink to={`/tenancies/${t.id}/edit`} icon="edit" variant="outline-primary">
                    Edit
                  </IconButtonLink>
                </Can>
                <Can permission="tenancies:delete">
                  <ConfirmButton
                    size="md"
                    title="Delete this tenancy?"
                    body="Tenancies with income recorded can't be deleted — end them instead."
                    onConfirm={async () => {
                      await api.delete(`/api/tenancies/${t.id}`);
                      await invalidate();
                      navigate("/tenancies");
                    }}
                  >
                    Delete
                  </ConfirmButton>
                </Can>
              </>
            }
          />
          <Row className="g-3 mb-4">
            <Col sm={6} xl={3}>
              <StatCard
                label="Current rent"
                value={t.currentRent ? <Money pence={t.currentRent.amountPence} /> : "Not set"}
                hint={t.currentRent ? f.label(RENT_FREQUENCIES, t.currentRent.frequency) : undefined}
                icon="income"
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                label="Rent received (to date)"
                value={<Money pence={t.arrears?.receivedPence ?? 0} kind="income" />}
                tone="income"
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                label="Outstanding"
                value={<Money pence={t.arrears?.outstandingPence ?? 0} kind="outstanding" />}
                tone="warning"
                hint={t.arrears?.creditPence ? `Credit ${f.money(t.arrears.creditPence)}` : undefined}
              />
            </Col>
            <Col sm={6} xl={3}>
              <StatCard
                label="Deposit"
                value={<Money pence={t.depositPence} />}
                hint={f.label(DEPOSIT_SCHEMES, t.depositScheme)}
              />
            </Col>
          </Row>
          <Row>
            <Col xl={8}>
              <Section title="Tenancy details">
                <DefinitionList
                  items={[
                    ["Start date", f.date(t.startDate)],
                    ["Fixed term ends", f.date(t.fixedTermEndDate)],
                    ["End date", f.date(t.endDate)],
                    ["Notice given", f.date(t.noticeGivenDate)],
                    t.endReason ? ["End reason", t.endReason] : null,
                    ["Reference", t.reference],
                    ["Deposit reference", t.depositReference],
                    ["Deposit protected", f.date(t.depositProtectedDate)],
                    t.depositReturnedDate
                      ? [
                          "Deposit returned",
                          `${f.date(t.depositReturnedDate)} · ${f.money(t.depositReturnedPence)}`,
                        ]
                      : null,
                    t.notes ? ["Notes", t.notes] : null,
                  ]}
                />
              </Section>
              <RentSection t={t} />
              <RentScheduleSection tenancyId={t.id} />
              <ReviewsSection t={t} />
              <Section title="Income received" flush>
                <IncomeTable filters={{ tenancyId: t.id }} showProperty={false} />
              </Section>
            </Col>
            <Col xl={4}>
              <Section title="Tenants">
                {t.tenants.map((tn) => (
                  <div key={tn.id} className="mb-3">
                    <Link to={`/tenants/${tn.id}`} className="fw-semibold">
                      {tn.name}
                    </Link>{" "}
                    {tn.isLead && (
                      <Badge bg="light" text="dark">
                        Lead
                      </Badge>
                    )}
                    <div className="small text-muted">
                      {[tn.email, tn.phone].filter(Boolean).join(" · ") || "No contact details"}
                    </div>
                  </div>
                ))}
              </Section>
              <Attachments entityType="tenancy" entityId={t.id} title="Tenancy documents" />
              <Section title="Communications" flush>
                <CommunicationTable filters={{ tenancyId: t.id }} compact />
              </Section>
              <Section title="Open tasks" flush>
                <TaskTable filters={{ tenancyId: t.id, view: "open" }} compact />
              </Section>
              <Section title="Maintenance" flush>
                <MaintenanceTable filters={{ tenancyId: t.id }} compact />
              </Section>
              <AuditHistory entityType="tenancy" entityId={t.id} />
            </Col>
          </Row>
        </>
      )}
    </QueryState>
  );
}
